import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { DeltaTone } from '@/components/dashboard/delta-badge'

import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DeltaBadge } from '@/components/dashboard/delta-badge'
import { Sparkline } from '@/components/charts/sparkline'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface KpiCardProps {
    label: string
    value: string
    icon: LucideIcon
    hint: string
    delta?: {
        value: string
        direction: 'up' | 'down' | 'flat'
        tone: DeltaTone
    }
    comparison?: string
    footnote?: ReactNode
    spark?: number[]
    sparkColor?: string
    className?: string
}

// Stat tile: the number is the hero, the sparkline only hints at the shape
export const KpiCard: FC<KpiCardProps> = ({
    label,
    value,
    icon: Icon,
    hint,
    delta,
    comparison,
    footnote,
    spark,
    sparkColor = 'var(--chart-1)',
    className
}): ReactNode => {
    return (
        <div className={cn('flex min-w-0 flex-col rounded-xl border bg-card p-5 text-card-foreground shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]', className)}>
            <div className='flex items-center justify-between gap-2'>
                <div className='flex min-w-0 items-center gap-2'>
                    <span className='flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground' aria-hidden='true'>
                        <Icon className='size-4' />
                    </span>

                    <h2 className='truncate text-sm font-medium text-muted-foreground'>
                        {label}
                    </h2>
                </div>

                <Tooltip>
                    <TooltipTrigger
                        className='flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50'
                        aria-label={`Tentang ${label}`}
                    >
                        <Info className='size-3.5' />
                    </TooltipTrigger>

                    <TooltipContent side='top' className='max-w-60 text-pretty'>
                        {hint}
                    </TooltipContent>
                </Tooltip>
            </div>

            <div className='mt-4 flex items-end justify-between gap-3'>
                <div className='min-w-0'>
                    <p className='font-heading text-[1.65rem] leading-none font-semibold tracking-tight text-foreground tabular-nums'>
                        {value}
                    </p>

                    {(delta || comparison) && (
                        <p className='mt-2.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground'>
                            {delta && <DeltaBadge value={delta.value} direction={delta.direction} tone={delta.tone} />}
                            {comparison && <span>{comparison}</span>}
                        </p>
                    )}

                    {footnote && (
                        <p className='mt-2.5 text-xs text-muted-foreground'>
                            {footnote}
                        </p>
                    )}
                </div>

                {spark && spark.length > 1 && (
                    <Sparkline values={spark} color={sparkColor} className='mb-0.5 h-10 w-20 shrink-0 sm:w-24' />
                )}
            </div>
        </div>
    )
}
